/**
 * Report History Component
 * Displays past trading reports with delivery status and pagination
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { getReportHistory, type ReportHistory } from '../../services/tradingReportsApi';
import { UserAwareCacheKeys } from '../../utils/userAwareCacheKeys';
import { useUserContext } from '../../hooks/useUserContext';
import { ReportViewerModal } from './ReportViewerModal';

// Format for report period display: "12 Feb 2025 6 AM"
const formatReportDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  return `${day} ${month} ${year} ${displayHours} ${ampm}`;
};

// Format report period with smart formatting for daily reports
const formatReportPeriod = (startStr: string, endStr: string, reportType: string): string => {
  const end = new Date(endStr);
  
  // For daily reports, show the end time with a note that it's the last 24 hours
  if (reportType === 'daily') {
    const day = end.getDate();
    const month = end.toLocaleDateString('en-US', { month: 'short' });
    const year = end.getFullYear();
    const hours = end.getHours();
    const minutes = end.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
    
    return `${day} ${month} ${year} ${displayHours}${displayMinutes} ${ampm} (Last 24 hours prior)`;
  }
  
  // Otherwise show full range with times
  return `${formatReportDateTime(startStr)} to ${formatReportDateTime(endStr)}`;
};

export const ReportHistoryComponent: React.FC = () => {
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [selectedReport, setSelectedReport] = useState<ReportHistory | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const { userId } = useUserContext();

  const { data, isLoading, error } = useQuery({
    queryKey: userId ? UserAwareCacheKeys.reportHistory(userId, limit, offset) : ['disabled'],
    queryFn: () => getReportHistory(limit, offset),
    enabled: !!userId,
    staleTime: 60 * 1000, // 1 minute
  });

  const handleNextPage = () => {
    if (data && offset + limit < data.total) {
      setOffset(offset + limit);
    }
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleViewReport = (report: ReportHistory) => {
    setSelectedReport(report);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setSelectedReport(null);
  };

  const getReportTypeLabel = (type: ReportHistory['report_type']) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      test: 'Test'
    };
    return labels[type] || type;
  };

  const getReportTypeBadgeColor = (type: ReportHistory['report_type']) => {
    const colors = {
      daily: 'bg-blue-500/20 text-blue-300 border-blue-400/30',
      weekly: 'bg-purple-500/20 text-purple-300 border-purple-400/30',
      monthly: 'bg-green-500/20 text-green-300 border-green-400/30',
      test: 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-300 border-gray-400/30';
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-white/70">Failed to load report history</p>
        <p className="text-sm text-white/50 mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  if (!data || data.reports.length === 0) {
    return (
      <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
        <ClockIcon className="w-16 h-16 text-white/30 mx-auto mb-4" />
        <p className="text-white/70 text-lg">No reports sent yet</p>
        <p className="text-sm text-white/50 mt-2">
          Your report history will appear here once reports are sent
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Report List */}
      <div className="space-y-3">
        {data.reports.map((report) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-colors"
          >
            {/* Header Row - Type, Period, and Action Button */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getReportTypeBadgeColor(report.report_type)}`}>
                  {getReportTypeLabel(report.report_type)}
                </span>
                <span className="text-sm text-white/70">
                  {formatReportPeriod(report.period_start, report.period_end, report.report_type)}
                </span>
              </div>
              
              {report.report_data && (
                <button
                  onClick={() => handleViewReport(report)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-300 bg-blue-600/20 border border-blue-500/30 rounded-lg hover:bg-blue-600/30 hover:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                >
                  <DocumentTextIcon className="w-3.5 h-3.5 mr-1" />
                  View Report
                </button>
              )}
            </div>

            {/* Delivery Status Row */}
            <div className="flex items-center justify-between pt-3 border-t border-white/5">
              <div className="flex items-center space-x-6">
              {/* Email Status */}
              <div className="flex items-center space-x-2">
                <EnvelopeIcon className="w-4 h-4 text-white/60" />
                {report.sent_via_email ? (
                  <div className="flex items-center space-x-2">
                    {report.email_error ? (
                      <div className="flex items-center space-x-1">
                        <XCircleIcon className="w-4 h-4 text-red-400" />
                        <span className="text-xs text-red-400">Failed</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-400" />
                        <span className="text-xs text-white/70">Sent</span>
                        {report.email_opened && (
                          <div className="flex items-center space-x-1">
                            <EyeIcon className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-xs text-blue-400">Opened</span>
                          </div>
                        )}
                        {report.email_clicked && (
                          <div className="flex items-center space-x-1">
                            <CursorArrowRaysIcon className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-xs text-purple-400">Clicked</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-white/40">Not sent</span>
                )}
              </div>

              {/* Telegram Status */}
              <div className="flex items-center space-x-2">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-white/60" />
                {report.sent_via_telegram ? (
                  <div className="flex items-center space-x-1">
                    {report.telegram_error ? (
                      <>
                        <XCircleIcon className="w-4 h-4 text-red-400" />
                        <span className="text-xs text-red-400">Failed</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4 text-green-400" />
                        <span className="text-xs text-white/70">Sent</span>
                      </>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-white/40">Not sent</span>
                )}
              </div>
              </div>
            </div>

            {/* Error Messages */}
            {(report.email_error || report.telegram_error) && (
              <div className="mt-3 pt-3 border-t border-white/10">
                {report.email_error && (
                  <div className="text-xs text-red-400 mb-1">
                    <span className="font-medium">Email Error:</span> {report.email_error}
                  </div>
                )}
                {report.telegram_error && (
                  <div className="text-xs text-red-400">
                    <span className="font-medium">Telegram Error:</span> {report.telegram_error}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {data.total > limit && (
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <div className="text-sm text-white/60">
            Showing {offset + 1} - {Math.min(offset + limit, data.total)} of {data.total}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={offset === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={offset + limit >= data.total}
              className="px-3 py-1.5 text-sm font-medium text-white bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Report Viewer Modal */}
      <ReportViewerModal
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        reportData={selectedReport?.report_data || null}
      />
    </div>
  );
};
